import axiosInstance from '../axiosInstance';

/**
 * 장바구니에서 예약 생성
 * @param {object} [options]
 * @param {boolean} [options.skipLoading] - true면 전역 로딩 오버레이를 띄우지 않는다.
 * @param {Array} [options.answers] - 예약 전 사전 질문 답변.
 *   [{ workshopId, items: [{ questionId, value }] }] — 한 요청이 여러 워크숍의
 *   예약을 만들 수 있어 워크숍별로 묶는다.
 */
export const createBookings = async (
  cartItemIds,
  customerName,
  customerEmail,
  { skipLoading = false, answers } = {},
) => {
  try {
    const response = await axiosInstance.post(
      '/bookings',
      {
        cartItemIds,
        customerName,
        customerEmail,
        ...(answers?.length ? { answers } : {}),
      },
      { skipLoading },
    );
    return response.data.data.bookingIds;
  } catch (error) {
    console.error('Create Bookings API Error:', error);
    throw error;
  }
};

/**
 * 모달에 넘길 답변 배열을 만든다.
 *
 * @param {number} workshopId
 * @param {object} answersByQuestionId  { [questionId]: '답변' }
 */
export const toWorkshopAnswers = (workshopId, answersByQuestionId) => {
  const items = Object.entries(answersByQuestionId ?? {})
    .filter(([, value]) => String(value ?? '').trim())
    .map(([questionId, value]) => ({
      questionId: Number(questionId),
      value: String(value).trim(),
    }));
  return items.length ? [{ workshopId, items }] : [];
};

/**
 * 예약 조회
 * @param {number} bookingId - 예약 ID
 */
export const getBooking = async (bookingId) => {
  try {
    const response = await axiosInstance.get(`/bookings/${bookingId}`);
    return response.data.data;
  } catch (error) {
    console.error('Get Booking API Error:', error);
    throw error;
  }
};

/**
 * 사용자 예약 목록 조회
 * @param {object} [options]
 * @param {boolean} [options.skipLoading] - true면 전역 로딩 오버레이를 띄우지 않는다.
 */
export const getUserBookings = async ({ skipLoading = false } = {}) => {
  try {
    const response = await axiosInstance.get('/bookings', { skipLoading });
    return response.data.data;
  } catch (error) {
    console.error('Get User Bookings API Error:', error);
    throw error;
  }
};

/**
 * 확인된 예약 목록 조회
 */
export const getConfirmedBookings = async () => {
  try {
    const response = await axiosInstance.get('/bookings/confirmed');
    return response.data.data;
  } catch (error) {
    console.error('Get Confirmed Bookings API Error:', error);
    throw error;
  }
};

/**
 * 예약 취소 (유저용)
 */
export const cancelBooking = async (
  bookingId,
  reason = 'Canceled by Customer',
) => {
  try {
    const response = await axiosInstance.post(`/bookings/${bookingId}/cancel`, {
      reason,
    });
    return response.data;
  } catch (error) {
    console.error('Cancel Booking API Error:', error);
    throw error;
  }
};

/**
 * 확정된 예약의 워크숍 상세 목록 조회 (페이징 적용)
 * @param {number} page - 요청할 페이지 번호 (0부터 시작)
 * @param {number} size - 한 페이지에 가져올 데이터 개수
 */
export const getConfirmedWorkshopDetails = async (
  page = 0,
  size = 3,
  status = 'ALL',
) => {
  try {
    const response = await axiosInstance.get('/bookings/confirmed-details', {
      params: { page, size, status },
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    console.error('Get Confirmed Workshop Details API Error:', error);
    throw error;
  }
};

/**
 * upcoming 예약 count 조회
 */
export const getUpcomingConfirmedCount = async () => {
  try {
    const response = await axiosInstance.get('/bookings/upcoming-count');
    return response.data.data;
  } catch (error) {
    console.error('Get Upcoming Confirmed Count API Error:', error);
    throw error;
  }
};

/**
 * 나의 전체 예약 수 조회
 */
export const getMyBookingCount = async () => {
  try {
    const response = await axiosInstance.get('/bookings/my-count');
    return response.data.data;
  } catch (error) {
    console.error('Get My Booking Count API Error:', error);
    throw error;
  }
};

/**
 * 타 유저의 전체 예약 수 조회
 */
export const getOtherBookingCount = async (userId) => {
  try {
    const response = await axiosInstance.get(`/bookings/other-count/${userId}`);
    return response.data.data;
  } catch (error) {
    console.error('Get My Booking Count API Error:', error);
    throw error;
  }
};

/**
 * 통계 조회
 * (CONFIRMED/CANCELED/ATTENDED 수, 이번달 결제액, 전월 대비 증감률)
 */
export const getAdminBookingStats = async () => {
  try {
    const response = await axiosInstance.get('/bookings/stats');
    return response.data.data;
  } catch (error) {
    console.error('Get Admin Booking Stats API Error:', error);
    throw error;
  }
};

/**
 * 출석 상태 변경 (CONFIRMED → ATTENDED)
 * @param {number} bookingId - 예약 ID
 * @param {string} currentStatus - 현재 상태
 */
export const markAsAttended = async (bookingId) => {
  try {
    const response = await axiosInstance.patch(`/bookings/${bookingId}/attend`);
    return response.data;
  } catch (error) {
    console.error('Mark As Attended API Error:', error);
    throw error;
  }
};

/**
 * 출석 상태 변경 (CONFIRMED or ATTENDED → ABSENT)
 * @param {number} bookingId - 예약 ID
 * @param {string} currentStatus - 현재 상태
 */
export const markAsAbsent = async (bookingId) => {
  try {
    const response = await axiosInstance.patch(`/bookings/${bookingId}/absent`);
    return response.data;
  } catch (error) {
    console.error('Mark As Attended API Error:', error);
    throw error;
  }
};

/**
 * 출석 상태 세분 처리 (Policy Handoff §1 attendance_status)
 * @param {number} bookingId
 * @param {('PENDING'|'ATTENDED'|'NO_SHOW'|'LATE')} attendanceStatus
 */
export const markAttendance = async (bookingId, attendanceStatus) => {
  try {
    const response = await axiosInstance.patch(
      `/bookings/${bookingId}/attendance`,
      { attendanceStatus },
    );
    return response.data;
  } catch (error) {
    console.error('Mark Attendance API Error:', error);
    throw error;
  }
};

/**
 * Confirmation 이메일 발송
 * CONFIRMED → 예약 확정 이메일
 * ATTENDED  → 참석 감사 이메일
 * @param {number} bookingId - 예약 ID
 */
export const sendConfirmationEmail = async (bookingId) => {
  try {
    const response = await axiosInstance.post(
      `/bookings/${bookingId}/confirmation-email`,
    );
    return response.data;
  } catch (error) {
    console.error('Send Confirmation Email API Error:', error);
    throw error;
  }
};

/**
 * 유저 Block
 * @param {number} bookingId - 예약 ID
 * @param {string} reason - block 사유
 */
export const blockUser = async (bookingId, reason = '') => {
  try {
    const response = await axiosInstance.post('/workshop-blocks/block', {
      bookingId,
      reason,
    });
    return response.data.data;
  } catch (error) {
    console.error('Block User API Error:', error);
    throw error;
  }
};

/**
 * 유저 Block 해제
 * @param {number} blockId - block ID
 */
export const unblockUser = async (blockId) => {
  try {
    const response = await axiosInstance.patch(
      `/workshop-blocks/${blockId}/unblock`,
    );
    return response.data;
  } catch (error) {
    console.error('Unblock User API Error:', error);
    throw error;
  }
};

/**
 * 유저 Block 횟수 +1
 */
export const blockUserAgain = async (blockId) => {
  try {
    await axiosInstance.post(`/workshop-blocks/${blockId}/block-again`);
  } catch (error) {
    console.error('Block Again API Error:', error);
    throw error;
  }
};

/**
 * 예약 취소 (어드민용)
 */
export const cancelBookingByAdmin = async (bookingId) => {
  try {
    await axiosInstance.patch(`/bookings/${bookingId}/admin-cancel`);
  } catch (error) {
    console.error('Cancel Booking API Error:', error);
    throw error;
  }
};

/**
 * 대기열 취소 (어드민용)
 */
export const removeFromWaitlist = async (bookingId) => {
  try {
    await axiosInstance.patch(`/bookings/${bookingId}/waitlist-remove`);
  } catch (error) {
    console.error('Remove from waitlist API Error:', error);
    throw error;
  }
};

/**
 * 대기열 순서 올리기 (UP)
 * @param {number} bookingId
 */
export const moveWaitlistUp = async (bookingId) => {
  try {
    const response = await axiosInstance.patch(
      `/bookings/${bookingId}/waitlist-up`,
    );
    return response.data;
  } catch (error) {
    console.error('Move Waitlist Up API Error:', error);
    throw error;
  }
};

/**
 * 대기열 순서 내리기 (DOWN)
 * @param {number} bookingId
 */
export const moveWaitlistDown = async (bookingId) => {
  try {
    const response = await axiosInstance.patch(
      `/bookings/${bookingId}/waitlist-down`,
    );
    return response.data;
  } catch (error) {
    console.error('Move Waitlist Down API Error:', error);
    throw error;
  }
};

/**
 * 오퍼 리마인드 메일 전송
 * @param {number} bookingId
 */
export const resendOffer = async (bookingId) => {
  try {
    await axiosInstance.post(`/bookings/${bookingId}/resend-offer`);
  } catch (error) {
    console.error('Resend Offer API Error:', error);
    throw error;
  }
};

/**
 * 임시좌석 생성
 * @param {number} bookingId
 */
export const offerSpot = async (bookingId) => {
  try {
    await axiosInstance.post(`/bookings/${bookingId}/offer-spot`);
  } catch (error) {
    console.error('Offer Spot API Error:', error);
    throw error;
  }
};

/**
 * 유저 환불
 * @param {number} invoiceNumber
 * @param {string} reason
 */
export const requestRefund = async (invoiceNumber, reason) => {
  const response = await axiosInstance.post(
    `/bookings/invoice/${invoiceNumber}/refund`,
    { reason },
  );
  return response.data;
};
